import { defineManifest } from '@ndrc/app-sdk/manifest';

/**
 * Simulateur d'appel — le Call Center du cahier des charges (§13).
 *
 * Un client est joué par un modèle de langue, il PARLE (voix de synthèse du navigateur) et son
 * texte s'affiche ; l'élève lui répond par écrit ; le client répond à son tour. À la fin de l'appel,
 * l'élève relit son échange en entier et reçoit un retour — **jamais une note** : ce que l'appel a
 * produit, ce qui aurait pu être dit autrement, et des questions auxquelles c'est à lui de répondre.
 *
 * **Cette app ne possède rien, et c'est le point.** Aucune table, aucune opération serveur, aucune
 * consigne envoyée à un modèle. Elle est une interface, plus DEUX chaînes de caractères envoyées à
 * une capacité nommée du cœur (`ctx.call`, ADR-015) : l'identifiant d'un correspondant choisi dans
 * le catalogue que le cœur lui a donné, et ce que l'élève vient d'écrire. Le personnage, sa
 * consigne, la conversation, son coût et son isolation entre élèves appartiennent tous au cœur.
 * C'est le §52 dans sa forme la plus nette : une app entière, ajoutée sans qu'une ligne du cœur ne
 * la nomme.
 *
 * **Aucune permission d'entité n'est demandée.** Le simulateur ne lit ni fiche, ni entreprise, ni
 * interaction : il n'a rien à faire dans le fichier client de l'élève. Une permission déclarée
 * « au cas où » serait une porte ouverte pour rien.
 *
 * **Le champ `ai` du manifeste n'est pas déclaré**, et c'est délibéré : sa forme
 * (`{ required: false, purposes }`) décrit une app qui commanderait elle-même des usages d'IA. Ce
 * n'est pas ce qui se passe ici — les deux usages employés (`core.call.reply`, `core.call.debrief`)
 * sont ceux du CŒUR, déclarés par lui, budgétés par lui. Les inscrire ici laisserait croire qu'une
 * app peut nommer un usage, donc en inventer un.
 *
 * **Son identité visuelle est à elle**, portée par ce manifeste — le cœur ne nomme jamais une app
 * (règle 1 de `CLAUDE.md`). `color` vient de la palette verrouillée
 * (`docs/design/desktop-reference.md`, `--blue-i`) : le bleu est la seule teinte de la palette qui
 * ne se confonde dans le dock ni avec le vert du Tableur ni avec l'orange du CRM, et c'est la
 * couleur qu'on associe au téléphone et au support. `glyph` est sa marque : un micro-casque —
 * serre-tête, deux écouteurs pleins, la perche du micro — dessiné en blanc sur ce bleu. Il dit
 * « on parle à quelqu'un » sans écrire le mot. `icon: 'phone'`, la forme générique déjà présente dans
 * la trousse de `@ndrc/ui`, reste déclarée comme repli si le glyphe venait à disparaître.
 */
export const manifest = defineManifest({
  id: 'appel',
  name: 'Appel',
  description:
    'Un client au téléphone, joué par l’IA : il parle, vous répondez, et vous relisez l’échange à la fin.',
  icon: 'phone',
  color: '#1D4ED8',
  glyph: [
    // Le serre-tête, d'une oreille à l'autre.
    { d: 'M5.2 13.6V12a6.8 6.8 0 0 1 13.6 0v1.6', width: 1.7 },
    // Les deux écouteurs, pleins : ce sont eux qu'on reconnaît à 40 px dans le dock.
    { d: 'M6.9 12.4H5.4a2 2 0 0 0-2 2v1.5a2 2 0 0 0 2 2h1.5z', fill: true },
    { d: 'M17.1 12.4h1.5a2 2 0 0 1 2 2v1.5a2 2 0 0 1-2 2h-1.5z', fill: true },
    // La perche du micro, qui descend vers la bouche.
    { d: 'M18.6 17.9v1.1a2 2 0 0 1-2 2h-2.2', width: 1.5, opacity: 0.9 },
  ],
  version: '0.1.0',
  sdk: '0.0.0',
  status: 'dev',
  category: 'prospection',
  permissions: { uses: [], defines: [] },
  entities: { uses: [], owns: [] },
  intents: { handles: [{ intent: 'core.call.open', route: '/' }] },
  events: { emits: [], subscribes: [] },
  ui: {
    student: {
      defaultRoute: '/',
      routes: [{ path: '/', title: 'Appel' }],
      layout: 'full',
    },
  },
  migrations: { schema: 'app_appel', dir: 'migrations' },
});
